import React from 'react';
import { Crown, Zap, Sparkles } from 'lucide-react';

export const BANNER_THEMES = {
  trial: {
    theme: 'trial' as const,
    emoji: '🎉',
    emojiAnimation: 'bounce' as const,
    title: 'Welcome to TinyScale Studio!',
    description: 'Your free trial is now active with 100 credits to explore our AI-powered image processing.',
    titleColor: 'text-white',
    descriptionColor: 'text-white/90'
  },
  trial_expired: {
    theme: 'trial_expired' as const,
    emoji: '⏰',
    emojiAnimation: 'pulse' as const,
    title: 'Trial expired',
    description: 'Your trial credits have been used up. Subscribe to continue transforming your product images with AI.',
    titleColor: 'text-white',
    descriptionColor: 'text-white/90'
  },
  limit_warning: {
    theme: 'warning' as const,
    emoji: '⚠️',
    emojiAnimation: 'pulse' as const,
    title: 'Approaching your plan limits',
    description: 'Consider upgrading to avoid interruptions.',
    titleColor: 'text-orange-800',
    descriptionColor: 'text-orange-700'
  },
  limit_reached: {
    theme: 'trial_expired' as const,
    emoji: '🚫',
    emojiAnimation: 'pulse' as const,
    title: 'Plan limits reached',
    description: 'You have reached your plan limits. Upgrade to continue using TinyScale Studio features.',
    titleColor: 'text-white',
    descriptionColor: 'text-white/90'
  },
  upgrade_recommendation: {
    theme: 'trial' as const,
    emoji: '⭐',
    emojiAnimation: 'bounce' as const,
    title: 'Upgrade recommended',
    description: 'Get more credits and features with a premium plan.',
    titleColor: 'text-white',
    descriptionColor: 'text-white/90'
  }
} as const;

export const FEATURE_SETS = {
  trial: [
    { 
      icon: React.createElement(Crown, { className: "h-6 w-6 text-yellow-400" }), 
      label: 'AI Processing' 
    },
    { 
      icon: React.createElement(Zap, { className: "h-6 w-6 text-green-400" }), 
      label: 'Instant Results' 
    },
    { 
      icon: React.createElement(Sparkles, { className: "h-6 w-6 text-pink-400" }), 
      label: 'No Limits' 
    }
  ],
  trial_expired: [
    { 
      icon: React.createElement(Crown, { className: "h-6 w-6 text-yellow-400" }), 
      label: 'Premium Plans' 
    },
    { 
      icon: React.createElement(Zap, { className: "h-6 w-6 text-green-400" }), 
      label: 'More Credits' 
    },
    { 
      icon: React.createElement(Sparkles, { className: "h-6 w-6 text-pink-400" }), 
      label: 'Full Features' 
    }
  ],
  warning: [
    { 
      icon: React.createElement(Crown, { className: "h-6 w-6 text-yellow-600" }), 
      label: 'More Credits' 
    },
    { 
      icon: React.createElement(Zap, { className: "h-6 w-6 text-green-600" }), 
      label: 'Higher Limits' 
    },
    { 
      icon: React.createElement(Sparkles, { className: "h-6 w-6 text-purple-600" }), 
      label: 'Premium Features' 
    }
  ],
  limit_reached: [
    { 
      icon: React.createElement(Crown, { className: "h-6 w-6 text-yellow-400" }), 
      label: 'Premium Plans' 
    },
    { 
      icon: React.createElement(Zap, { className: "h-6 w-6 text-green-400" }), 
      label: 'Unlimited Processing' 
    },
    { 
      icon: React.createElement(Sparkles, { className: "h-6 w-6 text-pink-400" }), 
      label: 'Advanced Features' 
    }
  ],
  upgrade_recommendation: [
    { 
      icon: React.createElement(Crown, { className: "h-6 w-6 text-yellow-400" }), 
      label: 'Higher Limits' 
    },
    { 
      icon: React.createElement(Zap, { className: "h-6 w-6 text-green-400" }), 
      label: 'Faster Processing' 
    },
    { 
      icon: React.createElement(Sparkles, { className: "h-6 w-6 text-pink-400" }), 
      label: 'Premium Support' 
    }
  ]
} as const;

export const ACTION_CONFIGS = {
  trial: {
    primary: {
      label: 'Explore Features',
      href: '/billing?tab=plans',
      variant: 'default',
      className: 'bg-white text-blue-600 hover:bg-gray-50 hover:text-blue-700 font-semibold shadow-lg transition-colors'
    },
    secondary: {
      label: 'Start Exploring',
      variant: 'ghost',
      className: 'text-white/90 hover:text-white hover:bg-white/20 font-medium'
    }
  },
  trial_expired: {
    primary: {
      label: 'Choose Plan',
      href: '/billing?tab=plans',
      variant: 'default',
      className: 'bg-white text-red-600 hover:bg-gray-50 hover:text-red-700 font-semibold shadow-lg transition-colors'
    },
    secondary: {
      label: 'Maybe Later',
      variant: 'ghost',
      className: 'text-white/90 hover:text-white hover:bg-white/20 font-medium'
    }
  },
  warning: {
    primary: {
      label: 'Upgrade Now',
      href: '/billing?tab=plans',
      variant: 'default',
      className: 'bg-orange-600 text-white hover:bg-orange-700 font-semibold shadow-lg transition-colors'
    },
    secondary: {
      label: 'Maybe Later',
      variant: 'ghost',
      className: 'text-orange-700 hover:text-orange-800 hover:bg-orange-100/20 font-medium'
    }
  },
  limit_reached: {
    primary: {
      label: 'Upgrade Plan',
      href: '/billing?tab=plans',
      variant: 'default',
      className: 'bg-white text-red-600 hover:bg-gray-50 hover:text-red-700 font-semibold shadow-lg transition-colors'
    },
    secondary: {
      label: 'Maybe Later',
      variant: 'ghost',
      className: 'text-white/90 hover:text-white hover:bg-white/20 font-medium'
    }
  },
  upgrade_recommendation: {
    primary: {
      label: 'View Plans',
      href: '/billing?tab=plans',
      variant: 'default',
      className: 'bg-white text-purple-600 hover:bg-gray-50 hover:text-purple-700 font-semibold shadow-lg transition-colors'
    },
    secondary: {
      label: 'Maybe Later',
      variant: 'ghost',
      className: 'text-white/90 hover:text-white hover:bg-white/20 font-medium'
    }
  }
} as const;

export type BannerThemeKey = keyof typeof BANNER_THEMES;
export type FeatureSetKey = keyof typeof FEATURE_SETS;
export type ActionConfigKey = keyof typeof ACTION_CONFIGS;